import { supabase } from './supabaseClient';
import type { Notification } from '../types/database';
import type { RealtimeChannel } from '@supabase/supabase-js';

export const notificationService = {
  async getRecent(limit = 30): Promise<Notification[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select(`
        *,
        actor:profiles!fk_notifications_actor_profile(id, full_name, avatar_url)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data || []) as Notification[];
  },

  async countUnread(): Promise<number> {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .is('read_at', null);

    if (error) throw error;
    return count || 0;
  },

  async markRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId);

    if (error) throw error;
  },

  async markAllRead(): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .is('read_at', null);

    if (error) throw error;
  },

  async resolveUserIdByMemberId(memberId: string, projectId: string): Promise<string | null> {
    const { data } = await supabase
      .from('team_members')
      .select('linked_user_id')
      .eq('id', memberId)
      .eq('project_id', projectId)
      .not('linked_user_id', 'is', null)
      .limit(1)
      .maybeSingle();

    return data?.linked_user_id || null;
  },

  async create(notification: {
    user_id: string;
    actor_id: string;
    type: string;
    project_id?: string;
    task_id?: string;
    comment_id?: string;
    /**
     * Chave estavel opcional. Com ela, o indice unico parcial
     * uniq_notifications_dedupe recusa a segunda notificacao igual para o
     * mesmo destinatario (erro 23505). Use namespace: 'capacity:<data>:<id>'.
     */
    dedupe_key?: string;
    data?: Record<string, unknown>;
  }): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .insert(notification);

    if (error) throw error;
  },

  subscribeToNew(userId: string, callback: (notification: Notification) => void): RealtimeChannel {
    return supabase
      .channel('notifications:' + userId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => callback(payload.new as Notification)
      )
      .subscribe();
  }
};
